import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { fetchAllUsers, approveUser, rejectUser, ADMIN_EMAIL } from '../../api';
import { playSound } from '../../lib/soundEffects';

const {
  FiShield,
  FiUserCheck,
  FiUserX,
  FiClock,
  FiRefreshCw,
  FiMail,
  FiCheck,
  FiX,
  FiActivity,
  FiServer,
  FiUserPlus,
  FiExternalLink,
} = FiIcons;

export default function TabAdmin({ theme, accent = '#10b981', user }) {
  const { textColor, mutedText, inputBg, inputBorder, surfaceBg, cardBg } = theme;

  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('pending'); // 'pending' | 'approved' | 'rejected'
  const [proxyStatus, setProxyStatus] = useState('checking'); // 'ok' | 'fallback' | 'checking'
  const [inviteEmail, setInviteEmail] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    try {
      playSound('click');
      const users = await fetchAllUsers();
      setAllUsers(users);
    } catch (e) {
      console.error('[Admin] Failed to load users:', e);
    } finally {
      setLoading(false);
    }
  };

  const checkProxyHealth = async () => {
    try {
      const res = await fetch('/api/data/read/folders_notely?limit=1');
      if (res.ok) {
        setProxyStatus('ok');
      } else {
        setProxyStatus('fallback');
      }
    } catch (_) {
      setProxyStatus('fallback');
    }
  };

  useEffect(() => {
    loadUsers();
    checkProxyHealth();
  }, []);

  const handleApprove = async (u) => {
    playSound('success');
    await approveUser(u.id);
    setActionSuccess(`Approved ${u.email}`);
    setTimeout(() => setActionSuccess(''), 3000);
    await loadUsers();
  };

  const handleReject = async (u) => {
    playSound('delete');
    await rejectUser(u.id);
    setActionSuccess(`Rejected/Revoked ${u.email}`);
    setTimeout(() => setActionSuccess(''), 3000);
    await loadUsers();
  };

  const pendingUsers = allUsers.filter((u) => u.role === 'pending');
  const approvedUsers = allUsers.filter((u) => u.role === 'approved');
  const rejectedUsers = allUsers.filter((u) => u.role === 'rejected');

  const displayedList =
    activeSubTab === 'pending'
      ? pendingUsers
      : activeSubTab === 'approved'
      ? approvedUsers
      : rejectedUsers;

  return (
    <div className="space-y-6">
      {/* Sovereign Admin Banner */}
      <div
        className="p-5 rounded-2xl border relative overflow-hidden flex flex-col md:flex-row md:items-center justify-between gap-4"
        style={{
          backgroundColor: surfaceBg,
          borderColor: 'rgba(16, 185, 129, 0.35)',
          boxShadow: '0 0 25px rgba(16, 185, 129, 0.08)',
        }}
      >
        <div className="flex items-center gap-3.5">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-lg"
            style={{
              background: `linear-gradient(135deg, ${accent}, #059669)`,
              color: '#000',
            }}
          >
            <SafeIcon icon={FiShield} className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold tracking-tight" style={{ color: textColor }}>
                Sovereign Administration Console
              </h3>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                👑 SUPERADMIN
              </span>
            </div>
            <p className="text-xs mt-0.5 font-mono" style={{ color: mutedText }}>
              Logged in as <span className="text-emerald-400 font-semibold">{user?.email || ADMIN_EMAIL}</span> • Status: <span className="text-emerald-400 font-semibold">Cloud Database Synced</span>
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={loadUsers}
          disabled={loading}
          className="px-3.5 py-2 rounded-xl border text-xs font-semibold flex items-center gap-2 transition-all hover:scale-105 active:scale-95 cursor-pointer shrink-0"
          style={{
            backgroundColor: cardBg,
            borderColor: inputBorder,
            color: textColor,
          }}
        >
          <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-emerald-400' : ''}`} />
          <span>Refresh Data</span>
        </button>
      </div>

      {/* Cloudflare Edge Gateway & Proxy Health Status */}
      <div
        className="p-4 rounded-2xl border grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs"
        style={{ backgroundColor: cardBg, borderColor: inputBorder }}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 shrink-0">
            <SafeIcon icon={FiServer} className="w-4 h-4" />
          </div>
          <div>
            <p className="font-semibold" style={{ color: textColor }}>Cloudflare Edge</p>
            <p className="text-[11px] font-mono text-emerald-400">app.memark.me (Pages)</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-cyan-500/15 text-cyan-400 border border-cyan-500/25 shrink-0">
            <SafeIcon icon={FiActivity} className="w-4 h-4" />
          </div>
          <div>
            <p className="font-semibold" style={{ color: textColor }}>Reverse Proxy Gateway</p>
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className={`w-2 h-2 rounded-full ${proxyStatus === 'ok' ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
              <span className="font-mono text-zinc-300">
                {proxyStatus === 'ok' ? '/api/* Active (Protected)' : 'Direct Fallback Active'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-purple-500/15 text-purple-400 border border-purple-500/25 shrink-0">
            <SafeIcon icon={FiUserCheck} className="w-4 h-4" />
          </div>
          <div>
            <p className="font-semibold" style={{ color: textColor }}>Pending Access</p>
            <p className="text-[11px] font-mono" style={{ color: pendingUsers.length > 0 ? '#F59E0B' : '#10B981' }}>
              {pendingUsers.length} user(s) awaiting approval
            </p>
          </div>
        </div>
      </div>

      {actionSuccess && (
        <div className="px-4 py-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold flex items-center gap-2">
          <SafeIcon icon={FiCheck} className="w-4 h-4 text-emerald-400" />
          <span>{actionSuccess}</span>
        </div>
      )}

      {/* User Management Section */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h4 className="text-sm font-bold tracking-tight" style={{ color: textColor }}>
            Access Requests &amp; User Accounts
          </h4>

          {/* Sub-Tabs: Pending / Approved / Rejected */}
          <div className="flex items-center gap-1 p-1 rounded-xl border shrink-0"
            style={{ backgroundColor: surfaceBg, borderColor: inputBorder }}>
            <button
              type="button"
              onClick={() => { playSound('click'); setActiveSubTab('pending'); }}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                activeSubTab === 'pending'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-xs'
                  : theme.isDark ? 'text-zinc-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <SafeIcon icon={FiClock} className="w-3.5 h-3.5 text-amber-400" />
              <span>Pending</span>
              {pendingUsers.length > 0 && (
                <span className="px-1.5 py-0.2 rounded-full text-[10px] font-mono bg-amber-500 text-black font-bold">
                  {pendingUsers.length}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => { playSound('click'); setActiveSubTab('approved'); }}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                activeSubTab === 'approved'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-xs'
                  : theme.isDark ? 'text-zinc-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <SafeIcon icon={FiUserCheck} className="w-3.5 h-3.5 text-emerald-400" />
              <span>Approved</span>
              <span className={`text-[10px] font-mono ${theme.isDark ? 'text-zinc-500' : 'text-slate-400'}`}>({approvedUsers.length})</span>
            </button>

            <button
              type="button"
              onClick={() => { playSound('click'); setActiveSubTab('rejected'); }}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                activeSubTab === 'rejected'
                  ? 'bg-red-500/20 text-red-300 border border-red-500/40 shadow-xs'
                  : theme.isDark ? 'text-zinc-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <SafeIcon icon={FiUserX} className="w-3.5 h-3.5 text-red-400" />
              <span>Rejected</span>
              <span className={`text-[10px] font-mono ${theme.isDark ? 'text-zinc-500' : 'text-slate-400'}`}>({rejectedUsers.length})</span>
            </button>
          </div>
        </div>

        {/* Users List Container */}
        <div
          className="rounded-2xl border divide-y overflow-hidden"
          style={{
            backgroundColor: cardBg,
            borderColor: inputBorder,
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <div className="w-6 h-6 rounded-full border-2 border-emerald-500/30 border-t-emerald-500 animate-spin" />
              <span className="text-xs font-mono" style={{ color: mutedText }}>
                Loading user accounts from cloud database...
              </span>
            </div>
          ) : displayedList.length === 0 ? (
            <div className="py-12 px-4 text-center">
              <SafeIcon icon={FiShield} className="w-8 h-8 mx-auto mb-2 text-zinc-600 opacity-60" />
              <p className="text-sm font-semibold" style={{ color: textColor }}>
                No {activeSubTab} users found
              </p>
              <p className="text-xs mt-1" style={{ color: mutedText }}>
                {activeSubTab === 'pending'
                  ? 'All sign-up access requests have been addressed.'
                  : `There are currently no accounts in the ${activeSubTab} tier.`}
              </p>
            </div>
          ) : (
            displayedList.map((u) => {
              const dateStr = u.created_at
                ? new Date(u.created_at).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })
                : 'Recent';

              return (
                <div
                  key={u.id || u.email}
                  className="flex items-center justify-between p-4 transition-colors hover:bg-white/[0.02]"
                  style={{ borderColor: inputBorder }}
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border"
                      style={{
                        backgroundColor:
                          activeSubTab === 'pending'
                            ? 'rgba(245, 158, 11, 0.15)'
                            : activeSubTab === 'approved'
                            ? 'rgba(16, 185, 129, 0.15)'
                            : 'rgba(239, 68, 68, 0.15)',
                        borderColor:
                          activeSubTab === 'pending'
                            ? 'rgba(245, 158, 11, 0.3)'
                            : activeSubTab === 'approved'
                            ? 'rgba(16, 185, 129, 0.3)'
                            : 'rgba(239, 68, 68, 0.3)',
                      }}
                    >
                      <SafeIcon
                        icon={FiMail}
                        className="w-4 h-4"
                        style={{
                          color:
                            activeSubTab === 'pending'
                              ? '#F59E0B'
                              : activeSubTab === 'approved'
                              ? '#10B981'
                              : '#EF4444',
                        }}
                      />
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs sm:text-sm font-bold truncate" style={{ color: textColor }}>
                          {u.email}
                        </p>
                        <span
                          className="px-2 py-0.2 rounded-full text-[9px] font-mono font-bold uppercase"
                          style={{
                            backgroundColor:
                              u.role === 'pending'
                                ? 'rgba(245, 158, 11, 0.15)'
                                : u.role === 'approved'
                                ? 'rgba(16, 185, 129, 0.15)'
                                : 'rgba(239, 68, 68, 0.15)',
                            color:
                              u.role === 'pending'
                                ? '#F59E0B'
                                : u.role === 'approved'
                                ? '#10B981'
                                : '#EF4444',
                          }}
                        >
                          {u.role || 'Member'}
                        </span>
                      </div>
                      <p className="text-[11px] font-mono mt-0.5" style={{ color: mutedText }}>
                        Requested on {dateStr}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {activeSubTab === 'pending' && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleApprove(u)}
                          className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-black flex items-center gap-1.5 transition-all shadow-[0_0_15px_rgba(16,185,129,0.2)] cursor-pointer"
                        >
                          <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                          <span>Approve</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReject(u)}
                          className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/30 flex items-center gap-1.5 transition-all cursor-pointer"
                        >
                          <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
                          <span>Reject</span>
                        </button>
                      </>
                    )}

                    {activeSubTab === 'approved' && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Revoke access for ${u.email}?`)) {
                            handleReject(u);
                          }
                        }}
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/25 flex items-center gap-1.5 transition-all cursor-pointer"
                      >
                        <SafeIcon icon={FiUserX} className="w-3.5 h-3.5" />
                        <span>Revoke Access</span>
                      </button>
                    )}

                    {activeSubTab === 'rejected' && (
                      <button
                        type="button"
                        onClick={() => handleApprove(u)}
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5 transition-all cursor-pointer"
                      >
                        <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                        <span>Re-Approve</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
